class ProductShowcaseControls {
  constructor(slider) {
    this.slider = slider;
    this.controls = slider.querySelector('.product-showcase__controls');
    this.useStableMobileControls = slider.dataset.stableMobileControls === 'true';

    if (!this.controls) return;

    this.updatePosition = this.updatePosition.bind(this);
    this.schedulePositionUpdate = this.schedulePositionUpdate.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.handleImageLoad = this.handleImageLoad.bind(this);

    this.slider.addEventListener('load', this.handleImageLoad, true);
    window.addEventListener('resize', this.handleResize);

    this.bindCarouselEvents();
    this.lockImageFrame();
    this.schedulePositionUpdate();
  }

  bindCarouselEvents() {
    const carousel = this.slider.sliderInstance?.slider;

    if (!carousel) {
      window.setTimeout(() => this.bindCarouselEvents(), 100);
      return;
    }

    carousel.on('realIndexChange', this.schedulePositionUpdate);
    carousel.on('slideChangeTransitionEnd', this.schedulePositionUpdate);
    this.schedulePositionUpdate();
  }

  schedulePositionUpdate() {
    window.requestAnimationFrame(this.updatePosition);
  }

  handleImageLoad() {
    this.lockImageFrame();
    this.schedulePositionUpdate();
  }

  handleResize() {
    this.slider.style.removeProperty('--product-showcase-image-width');
    this.slider.style.removeProperty('--product-showcase-image-height');
    this.slider.style.removeProperty('--product-showcase-image-ratio');

    window.requestAnimationFrame(() => {
      this.lockImageFrame();
      this.updatePosition();
    });
  }

  lockImageFrame() {
    const firstImageFrame =
      this.slider.querySelector('.swiper-slide:not(.swiper-slide-duplicate)[data-index="0"] .product-showcase__product-image-link') ||
      this.slider.querySelector('.product-showcase__product-image-link');

    if (!firstImageFrame) return;

    const frameBounds = firstImageFrame.getBoundingClientRect();
    if (!frameBounds.width || !frameBounds.height) return;

    this.slider.style.setProperty('--product-showcase-image-width', `${frameBounds.width}px`);
    this.slider.style.setProperty('--product-showcase-image-height', `${frameBounds.height}px`);
    this.slider.style.setProperty('--product-showcase-image-ratio', 'auto');
  }

  updatePosition() {
    const useStableReference =
      this.useStableMobileControls && window.matchMedia('(max-width: 767px)').matches;
    const activeImage = useStableReference
      ? this.slider.querySelector(
          '.swiper-slide:not(.swiper-slide-duplicate)[data-index="0"] .product-showcase__product-image-link'
        )
      : this.slider.querySelector('.swiper-slide-active .product-showcase__product-image-link');

    if (!activeImage) return;

    const sliderBounds = this.slider.getBoundingClientRect();
    const imageBounds = activeImage.getBoundingClientRect();
    const previousButton = this.controls.querySelector('.swiper-button-prev');
    const buttonWidth = previousButton?.getBoundingClientRect().width || 32;
    const iconWidth = previousButton?.querySelector('svg')?.getBoundingClientRect().width || 16;
    const imageArrowGap = 32;

    this.controls.style.setProperty(
      '--product-showcase-controls-top',
      `${imageBounds.top - sliderBounds.top + imageBounds.height / 2}px`
    );
    this.controls.style.setProperty(
      '--product-showcase-controls-left',
      `${imageBounds.left - sliderBounds.left - imageArrowGap - (buttonWidth + iconWidth) / 2}px`
    );
    this.controls.style.setProperty(
      '--product-showcase-controls-width',
      `${imageBounds.width + buttonWidth + iconWidth + imageArrowGap * 2}px`
    );
  }
}

const initializeMobileSceneLinks = (slider) => {
  const sceneLinks = slider.querySelectorAll('[data-mobile-product-link]');

  if (!sceneLinks.length || slider.dataset.mobileSceneLinksReady === 'true') return;

  const mobileMediaQuery = window.matchMedia('(max-width: 767px)');
  const updateLinks = () => {
    sceneLinks.forEach((link) => {
      if (mobileMediaQuery.matches) {
        link.setAttribute('href', link.dataset.mobileProductLink);
      } else {
        link.removeAttribute('href');
      }
    });
  };

  slider.dataset.mobileSceneLinksReady = 'true';
  if (mobileMediaQuery.addEventListener) {
    mobileMediaQuery.addEventListener('change', updateLinks);
  } else {
    mobileMediaQuery.addListener(updateLinks);
  }
  updateLinks();
};

const initializeProductShowcaseControls = (root = document) => {
  const sliders = root.matches?.('.product-showcase__slider')
    ? [root]
    : root.querySelectorAll('.product-showcase__slider');

  sliders.forEach((slider) => {
    initializeMobileSceneLinks(slider);

    if (slider.dataset.productShowcaseControlsReady === 'true') return;

    slider.dataset.productShowcaseControlsReady = 'true';
    new ProductShowcaseControls(slider);
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeProductShowcaseControls);
} else {
  initializeProductShowcaseControls();
}

document.addEventListener('shopify:section:load', (event) => {
  initializeProductShowcaseControls(event.target);
});
